import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Calendar, AlertTriangle, TrendingUp, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTRACT_TYPE_OPTIONS = ['ROZ', 'Maatwerk', 'Geen', 'Onbekend'];
const INDEXATIE_TYPE_OPTIONS = ['CPI', 'Vast %', 'Geen', 'Onbekend'];
const LEEGSTAND_RISICO_OPTIONS = [
  { value: 'Laag', color: 'bg-green-500' },
  { value: 'Midden', color: 'bg-yellow-500' },
  { value: 'Hoog', color: 'bg-red-500' },
  { value: 'Onbekend', color: 'bg-slate-500' }
];

export const LeasesTab = ({ buildingId, units = [], tenants = [], wale, onLeasesChange, t }) => {
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [form, setForm] = useState({
    unit_id: '',
    tenant_id: '',
    contract_type: 'Onbekend',
    startdatum: '',
    einddatum: '',
    huurprijs_eur_per_maand: '',
    huurprijs_eur_per_jaar: '',
    indexatie_type: 'Onbekend',
    indexatie_pct: '',
    servicekosten_eur_per_jaar: '',
    huurvrije_periode_maanden: '',
    break_optie: false,
    break_datum: '',
    waarborgsom_eur: '',
    leegstand_risico: 'Onbekend',
    is_vacancy: false
  });

  const fetchLeases = useCallback(async () => {
    try {
      setLoading(true);
      
      const res = await axios.get(`${API_URL}/api/buildings/${buildingId}/leases`, {
        withCredentials: true
      });
      setLeases(res.data);
      if (onLeasesChange) onLeasesChange(res.data);
    } catch (err) {
      console.error('Error fetching leases:', err);
    } finally {
      setLoading(false);
    }
  }, [buildingId, onLeasesChange]);

  useEffect(() => {
    if (buildingId) {
      fetchLeases();
    }
  }, [buildingId, fetchLeases]);

  const resetForm = () => {
    setForm({
      unit_id: '',
      tenant_id: '',
      contract_type: 'Onbekend',
      startdatum: '',
      einddatum: '',
      huurprijs_eur_per_maand: '',
      huurprijs_eur_per_jaar: '',
      indexatie_type: 'Onbekend',
      indexatie_pct: '',
      servicekosten_eur_per_jaar: '',
      huurvrije_periode_maanden: '',
      break_optie: false,
      break_datum: '',
      waarborgsom_eur: '',
      leegstand_risico: 'Onbekend',
      is_vacancy: false
    });
    setEditingLease(null);
  };

  const openDialog = (lease = null) => {
    if (lease) {
      setEditingLease(lease);
      setForm({
        unit_id: lease.unit_id || '',
        tenant_id: lease.tenant_id || '',
        contract_type: lease.contract_type || 'Onbekend',
        startdatum: lease.startdatum?.split('T')[0] || '',
        einddatum: lease.einddatum?.split('T')[0] || '',
        huurprijs_eur_per_maand: lease.huurprijs_eur_per_maand || '',
        huurprijs_eur_per_jaar: lease.huurprijs_eur_per_jaar || '',
        indexatie_type: lease.indexatie_type || 'Onbekend',
        indexatie_pct: lease.indexatie_pct || '',
        servicekosten_eur_per_jaar: lease.servicekosten_eur_per_jaar || '',
        huurvrije_periode_maanden: lease.huurvrije_periode_maanden || '',
        break_optie: lease.break_optie || false,
        break_datum: lease.break_datum?.split('T')[0] || '',
        waarborgsom_eur: lease.waarborgsom_eur || '',
        leegstand_risico: lease.leegstand_risico || 'Onbekend',
        is_vacancy: lease.is_vacancy || false
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    

    const payload = {
      ...form,
      huurprijs_eur_per_maand: form.huurprijs_eur_per_maand ? parseFloat(form.huurprijs_eur_per_maand) : null,
      huurprijs_eur_per_jaar: form.huurprijs_eur_per_jaar ? parseFloat(form.huurprijs_eur_per_jaar) : null,
      indexatie_pct: form.indexatie_pct ? parseFloat(form.indexatie_pct) : null,
      servicekosten_eur_per_jaar: form.servicekosten_eur_per_jaar ? parseFloat(form.servicekosten_eur_per_jaar) : null,
      huurvrije_periode_maanden: form.huurvrije_periode_maanden ? parseFloat(form.huurvrije_periode_maanden) : null,
      waarborgsom_eur: form.waarborgsom_eur ? parseFloat(form.waarborgsom_eur) : null
    };

    try {
      if (editingLease) {
        await axios.put(`${API_URL}/api/leases/${editingLease.lease_id}`, payload, {
          withCredentials: true
        });
        toast.success('Huurcontract bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/units/${form.unit_id}/leases`, payload, {
          withCredentials: true
        });
        toast.success('Huurcontract toegevoegd');
      }
      setDialogOpen(false);
      resetForm();
      fetchLeases();
    } catch (err) {
      console.error('Error saving lease:', err);
      toast.error('Fout bij opslaan huurcontract');
    }
  };

  const handleDelete = async (leaseId) => {
    if (!window.confirm('Weet je zeker dat je dit huurcontract wilt verwijderen?')) {
      return;
    }
    
    try {
      
      await axios.delete(`${API_URL}/api/leases/${leaseId}`, {
        withCredentials: true
      });
      toast.success('Huurcontract verwijderd');
      fetchLeases();
    } catch (err) {
      console.error('Error deleting lease:', err);
      toast.error('Fout bij verwijderen huurcontract');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `€ ${parseFloat(value).toLocaleString('nl-NL', { minimumFractionDigits: 0 })}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('nl-NL');
  };

  const getRisicoBadge = (risico) => {
    const opt = LEEGSTAND_RISICO_OPTIONS.find(o => o.value === risico);
    return (
      <Badge className={`${opt?.color || 'bg-slate-500'} text-white`}>
        {risico}
      </Badge>
    );
  };

  // Calculate totals
  const totalAnnualRent = leases.reduce((sum, l) => sum + (l.totale_huur_per_jaar || l.huurprijs_eur_per_jaar || (l.huurprijs_eur_per_maand ? l.huurprijs_eur_per_maand * 12 : 0)), 0);
  const activeLeases = leases.filter(l => !l.is_vacancy).length;

  return (
    <div className="space-y-4" data-testid="leases-tab">
      {/* Summary with WALE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-slate-400 text-sm">Huurcontracten</span>
                <p className="text-2xl font-bold text-white">{leases.length}</p>
              </div>
              <div className="border-l border-slate-700 pl-6">
                <span className="text-slate-400 text-sm">Actief</span>
                <p className="text-2xl font-bold text-green-400">{activeLeases}</p>
              </div>
              <div className="border-l border-slate-700 pl-6">
                <span className="text-slate-400 text-sm">Totaal Huur/Jaar</span>
                <p className="text-xl font-bold text-white">{formatCurrency(totalAnnualRent)}</p>
              </div>
            </div>
            <Button 
              onClick={() => openDialog()} 
              className="bg-[#b4976f] hover:bg-[#a3865f] text-white"
              data-testid="add-lease-btn"
              disabled={units.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Contract Toevoegen
            </Button>
          </div>
        </div>

        {/* WALE Display */}
        <div className="bg-gradient-to-r from-slate-800/50 to-[#b4976f]/20 rounded-lg p-4 border border-[#b4976f]/30">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="h-5 w-5 text-[#b4976f]" />
            <span className="text-slate-300 font-medium">WALE (Weighted Average Lease Expiry)</span>
          </div>
          {wale && wale.wale_years !== null ? (
            <div className="flex items-center gap-6">
              <div>
                <p className="text-3xl font-bold text-white">{wale.wale_years} <span className="text-lg text-slate-400">jaar</span></p>
                <p className="text-sm text-slate-400">{wale.wale_months} maanden</p>
              </div>
              <div className="border-l border-slate-700 pl-6">
                <Badge 
                  className={`${
                    wale.calculation_method === 'Berekend_op_huur' ? 'bg-green-600' :
                    wale.calculation_method === 'Berekend_op_m2' ? 'bg-blue-600' :
                    wale.calculation_method === 'Handmatig' ? 'bg-purple-600' : 'bg-slate-600'
                  }`}
                >
                  {wale.calculation_method.replace(/_/g, ' ')}
                </Badge>
                <p className="text-xs text-slate-500 mt-1">{wale.lease_count} contracten</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-slate-400">
              <AlertTriangle className="h-4 w-4" />
              <span>Onvoldoende data voor WALE berekening</span>
            </div>
          )}
        </div>
      </div>

      {/* No units warning */}
      {units.length === 0 && (
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400" />
          <span className="text-amber-300">Voeg eerst units toe voordat je huurcontracten kunt registreren.</span>
        </div>
      )}

      {/* Leases Table */}
      {loading && leases.length === 0 ? (
        <div className="text-center py-8 text-slate-400">Laden...</div>
      ) : leases.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/30 rounded-lg border border-dashed border-slate-700">
          <FileText className="h-12 w-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 mb-4">Nog geen huurcontracten geregistreerd</p>
          {units.length > 0 && (
            <Button 
              onClick={() => openDialog()}
              variant="outline"
              className="border-[#b4976f] text-[#b4976f] hover:bg-[#b4976f]/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Eerste Contract Toevoegen
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800 hover:bg-slate-800">
                <TableHead className="text-slate-300">Unit</TableHead>
                <TableHead className="text-slate-300">Huurder</TableHead>
                <TableHead className="text-slate-300">Contract</TableHead>
                <TableHead className="text-slate-300 text-right">Huur/Jaar</TableHead>
                <TableHead className="text-slate-300">Looptijd</TableHead>
                <TableHead className="text-slate-300">Risico</TableHead>
                <TableHead className="text-slate-300 text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leases.map((lease) => (
                <TableRow key={lease.lease_id} className="border-slate-700 hover:bg-slate-800/50">
                  <TableCell className="text-white font-medium">
                    {lease.unit_naam || units.find(u => u.unit_id === lease.unit_id)?.unit_naam || lease.unit_id}
                  </TableCell>
                  <TableCell className="text-slate-300">
                    {lease.is_vacancy ? (
                      <Badge className="bg-red-500">Leegstand</Badge>
                    ) : (
                      lease.tenant_naam || tenants.find(t => t.tenant_id === lease.tenant_id)?.tenant_naam || '-'
                    )}
                  </TableCell>
                  <TableCell className="text-slate-300">{lease.contract_type}</TableCell>
                  <TableCell className="text-slate-300 text-right font-medium">
                    {formatCurrency(lease.totale_huur_per_jaar || lease.huurprijs_eur_per_jaar)}
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    <div>{formatDate(lease.startdatum)} - {formatDate(lease.einddatum)}</div>
                    {lease.looptijd_maanden && (
                      <div className="text-slate-500 text-xs">{lease.looptijd_maanden} mnd</div>
                    )}
                  </TableCell>
                  <TableCell>{getRisicoBadge(lease.leegstand_risico)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDialog(lease)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(lease.lease_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Lease Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingLease ? 'Huurcontract Bewerken' : 'Nieuw Huurcontract'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Unit & Tenant */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Unit *</Label>
                <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })} required>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="lease-unit-select">
                    <SelectValue placeholder="Selecteer unit" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {units.map((unit) => (
                      <SelectItem key={unit.unit_id} value={unit.unit_id} className="text-white hover:bg-slate-800">
                        {unit.unit_naam} ({unit.unit_oppervlakte_m2 || 0} m²)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Huurder</Label>
                <Select value={form.tenant_id || '__vacancy__'} onValueChange={(v) => setForm({ ...form, tenant_id: v === '__vacancy__' ? '' : v, is_vacancy: v === '__vacancy__' })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="lease-tenant-select">
                    <SelectValue placeholder="Selecteer huurder (of leeg)" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="__vacancy__" className="text-slate-400 hover:bg-slate-800">
                      Geen (Leegstand)
                    </SelectItem>
                    {tenants.map((tenant) => (
                      <SelectItem key={tenant.tenant_id} value={tenant.tenant_id} className="text-white hover:bg-slate-800">
                        {tenant.tenant_naam}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Contract Type & Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Contract Type</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {CONTRACT_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Startdatum</Label>
                <Input
                  type="date"
                  value={form.startdatum}
                  onChange={(e) => setForm({ ...form, startdatum: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="lease-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Einddatum</Label>
                <Input
                  type="date"
                  value={form.einddatum}
                  onChange={(e) => setForm({ ...form, einddatum: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="lease-end-input"
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Huur/Maand (€)</Label>
                <Input
                  type="number"
                  value={form.huurprijs_eur_per_maand}
                  onChange={(e) => setForm({ 
                    ...form, 
                    huurprijs_eur_per_maand: e.target.value,
                    huurprijs_eur_per_jaar: e.target.value ? (parseFloat(e.target.value) * 12).toString() : ''
                  })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="0"
                  data-testid="lease-rent-month-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Huur/Jaar (€)</Label>
                <Input
                  type="number"
                  value={form.huurprijs_eur_per_jaar}
                  onChange={(e) => setForm({ ...form, huurprijs_eur_per_jaar: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="0"
                  data-testid="lease-rent-year-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Servicekosten/Jaar (€)</Label>
                <Input
                  type="number"
                  value={form.servicekosten_eur_per_jaar}
                  onChange={(e) => setForm({ ...form, servicekosten_eur_per_jaar: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Indexation */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Indexatie Type</Label>
                <Select value={form.indexatie_type} onValueChange={(v) => setForm({ ...form, indexatie_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {INDEXATIE_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.indexatie_type === 'Vast %' && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Indexatie %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.indexatie_pct}
                    onChange={(e) => setForm({ ...form, indexatie_pct: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="2.5"
                  />
                </div>
              )}
            </div>

            {/* Break Option */}
            <div className="space-y-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Break Optie</Label>
                <Switch
                  checked={form.break_optie}
                  onCheckedChange={(v) => setForm({ ...form, break_optie: v })}
                />
              </div>
              {form.break_optie && (
                <div className="space-y-2">
                  <Label className="text-slate-400 text-sm">Break Datum</Label>
                  <Input
                    type="date"
                    value={form.break_datum}
                    onChange={(e) => setForm({ ...form, break_datum: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              )}
            </div>

            {/* Other fields */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Waarborgsom (€)</Label>
                <Input
                  type="number"
                  value={form.waarborgsom_eur}
                  onChange={(e) => setForm({ ...form, waarborgsom_eur: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Huurvrije Periode (mnd)</Label>
                <Input
                  type="number"
                  value={form.huurvrije_periode_maanden}
                  onChange={(e) => setForm({ ...form, huurvrije_periode_maanden: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Leegstand Risico</Label>
                <Select value={form.leegstand_risico} onValueChange={(v) => setForm({ ...form, leegstand_risico: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {LEEGSTAND_RISICO_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-800">{opt.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">
                Annuleren
              </Button>
              <Button type="submit" className="bg-[#b4976f] hover:bg-[#a3865f] text-white" data-testid="save-lease-btn">
                {editingLease ? 'Bijwerken' : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeasesTab;
